import * as React from "react";
import { Link } from "react-router";
import { AtSign, Bell, Check, FileText, Megaphone, Settings, Ticket } from "lucide-react";

import type { NotificationRow as Row } from "@/api/notifications";
import { cn } from "@/lib/cn";
import { rowHref as sharedRowHref } from "@/lib/notificationRoute";
import { useViewport } from "@/lib/useViewport";

/**
 * ops/Bell · ops/Notifications (MV2-878) — the one notification row, shared by
 * the bell popover and the full page: unread dot (action colour, transparent
 * once read) · category icon · title + one-line snippet with the site · when ·
 * an optional "Mark read" tick (page only). Click marks read and navigates;
 * middle-click follows the link without marking.
 */

const ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  mention: AtSign,
  ticket: Ticket,
  alarm: Bell,
  report: FileText,
  export: FileText,
  system: Megaphone,
  account: Settings,
};

export function relativeTime(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 60_000) return "just now";
  const min = Math.floor(diffMs / 60_000);
  if (min < 60) return `${min} m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.floor(hr / 24);
  if (day < 2) return d.toLocaleString("en-IE", { weekday: "short", hour: "2-digit", minute: "2-digit" });
  if (day < 7) return `${day} d ago`;
  return d.toLocaleDateString("en-IE", { day: "numeric", month: "short" });
}

// MV2-340: routing lives in `lib/notificationRoute` so the mobile FCM tap handler shares it.
export function rowHref(row: Row): string {
  return sharedRowHref({
    category: row.category,
    event: row.event,
    related_type: row.related_type,
    related_id: row.related_id,
    payload: row.payload,
  });
}

interface Props {
  row: Row;
  onClick?: (row: Row) => void;
  /** Override the link destination (e.g. to preserve URL search params). */
  to?: string;
  /** Page only — the tick at the end of the row. */
  onMarkRead?: (row: Row) => void;
  posInSet?: number;
  setSize?: number;
}

export const NotificationRow: React.FC<Props> = ({ row, onClick, to, onMarkRead, posInSet, setSize }) => {
  const { isMobile } = useViewport();
  const Icon = ICONS[row.category] ?? Bell;
  const isUnread = row.read_at === null;
  return (
    <article
      role="article"
      aria-posinset={posInSet}
      aria-setsize={setSize}
      className={cn("group relative flex items-start gap-[11px] border-b border-gray-100 px-3.5 last:border-b-0 hover:bg-gray-50", isMobile ? "py-3" : "py-[11px]")}
    >
      <span aria-hidden className="mt-[6px] h-[7px] w-[7px] flex-none rounded-full" style={{ background: isUnread ? "var(--action)" : "transparent" }} />
      <Icon size={14} className="mt-px flex-none text-gray-500" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <Link to={to ?? rowHref(row)} onClick={() => onClick?.(row)} className={cn("line-clamp-2 text-left text-[12.5px] leading-snug text-gray-900", isUnread ? "font-semibold" : "font-medium")}>
          {row.title || row.subject || "Notification"}
        </Link>
        {(row.snippet || row.site_name) && (
          <span className="line-clamp-2 text-[11px] leading-[1.4] text-gray-600">
            {row.snippet}
            {row.snippet && row.site_name ? " · " : ""}
            {row.site_name ? (
              row.site_id ? (
                <Link to={`/notifications?site_id=${encodeURIComponent(row.site_id)}`} aria-label={`Filter notifications by site ${row.site_name}`} className="underline decoration-dotted underline-offset-2 hover:text-gray-900">
                  {row.site_name}
                </Link>
              ) : (
                <span>{row.site_name}</span>
              )
            ) : null}
          </span>
        )}
      </div>
      <time dateTime={row.created_at} title={new Date(row.created_at).toLocaleString("en-IE")} className="tabular flex-none whitespace-nowrap text-[11px] text-gray-500">
        {relativeTime(row.created_at)}
      </time>
      {onMarkRead && (
        <button
          type="button"
          onClick={() => onMarkRead(row)}
          disabled={!isUnread}
          title={isUnread ? "Mark read" : "Read"}
          aria-label={isUnread ? `Mark "${row.title}" read` : "Already read"}
          className={cn("flex-none rounded-[4px] p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-focus", isUnread ? "text-gray-500 hover:text-gray-900" : "text-gray-300")}
        >
          <Check size={13} aria-hidden />
        </button>
      )}
    </article>
  );
};
